'use client'

import {
  createContext,
  PropsWithChildren,
  Suspense,
  use,
  useContext,
  useEffect,
  useState,
} from 'react'

interface WorldIframeTokenContextType {
  jwtToken: string | null
}

const WorldIframeTokenContext = createContext<WorldIframeTokenContextType | undefined>(undefined)

type WorldIframeTokenProviderProps = PropsWithChildren<{
  tokenPromise: Promise<string | null>
}>

type TokenBridgeProps = {
  tokenPromise: Promise<string | null>
  onToken: (token: string | null) => void
}

function TokenBridge({ tokenPromise, onToken }: TokenBridgeProps) {
  const token = use(tokenPromise)

  useEffect(() => {
    onToken(token)
  }, [token, onToken])

  return null
}

export function WorldIframeTokenProvider({
  tokenPromise,
  children,
}: WorldIframeTokenProviderProps) {
  const [jwtToken, setJwtToken] = useState<string | null>(null)

  return (
    <WorldIframeTokenContext.Provider value={{ jwtToken }}>
      {children}
      <Suspense fallback={null}>
        <TokenBridge tokenPromise={tokenPromise} onToken={setJwtToken} />
      </Suspense>
    </WorldIframeTokenContext.Provider>
  )
}

export function useWorldIframeToken() {
  const context = useContext(WorldIframeTokenContext)
  if (context === undefined) {
    throw new Error('useWorldIframeToken must be used within a WorldIframeTokenProvider')
  }
  return context
}
